import { useState, useCallback, useRef } from "react";

/**
 * useConfirm — Hook para usar `<ConfirmDialog>` de forma imperativa.
 *
 * Uso:
 *   const { confirm, dialogProps } = useConfirm();
 *   // ... más arriba en el JSX
 *   <ConfirmDialog {...dialogProps} />
 *
 *   // y en tu handler:
 *   const ok = await confirm({
 *     title: "Eliminar usuario",
 *     description: "¿Confirmas eliminar a juan@example.com?",
 *     confirmLabel: "Eliminar",
 *     destructive: true,
 *     testIdPrefix: "delete-user",
 *   });
 *   if (!ok) return;
 *   // ... acción
 *
 * Reemplaza `window.confirm()` por un modal Shadcn con misma semántica.
 */
export function useConfirm() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const [loading, setLoading] = useState(false);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts(options);
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      // Si el caller pasó `onConfirm` lo ejecutamos; si no, simplemente resolvemos true.
      if (typeof opts.onConfirm === "function") {
        await opts.onConfirm();
      }
      resolverRef.current?.(true);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }, [opts]);

  const handleOpenChange = useCallback((newOpen) => {
    if (!newOpen) {
      // Cierre vía X / cancel → resolver false
      resolverRef.current?.(false);
      resolverRef.current = null;
    }
    setOpen(newOpen);
  }, []);

  const dialogProps = {
    open,
    onOpenChange: handleOpenChange,
    title: opts.title || "Confirmar acción",
    description: opts.description,
    confirmLabel: opts.confirmLabel,
    cancelLabel: opts.cancelLabel,
    destructive: opts.destructive,
    testIdPrefix: opts.testIdPrefix || "confirm",
    loading,
    onConfirm: handleConfirm,
  };

  return { confirm, dialogProps };
}

export default useConfirm;
